import React, { useState, useEffect } from "react";
import { Row, Col, Input, Button, Spin, message, Popover } from "antd";
import { EnvironmentOutlined, DollarOutlined, ClockCircleOutlined, DownOutlined, ShareAltOutlined, LinkOutlined, LinkedinOutlined, TwitterOutlined } from '@ant-design/icons';
import { axiosInstant } from 'src/api/api';
import { useNavigate } from 'react-router-dom';
import { useUser } from 'src/context/auth.context';
import { ROUTES } from '../common/constants';
import Navbar from '../NavBar';
import Footer from '../Footer';
import AuthFormModal from '../AuthPanel/AuthFormModal';
import styled from "styled-components";

const { Search } = Input;

// Styled Components
const JobBoardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  background-color: #f8fafb;
  min-height: 100vh;
`;

const SearchSection = styled.div`
  background: linear-gradient(135deg, #efc73c 0%, #bb9638 100%);
  padding: 60px 40px;
  border-radius: 20px;
  margin-bottom: 40px;
  text-align: center;
  color: #333;
  box-shadow: 0 8px 32px rgba(239, 199, 60, 0.3);
`;

const FilterSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 16px;
  margin-bottom: 30px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
`;


const ApplyButton = styled(Button)`
  background: linear-gradient(135deg, #efc73c 0%, #bb9638 100%);
  border: none;
  border-radius: 8px;
  height: 48px;
  font-weight: 600;
  font-size: 17px;
  color: #333;
  box-shadow: 0 2px 8px rgba(239, 199, 60, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, #f5d55f 0%, #c9a045 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 199, 60, 0.4);
    color: #333;
  }
`;

const JobHeaderCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border-left: 4px solid #efc73c;
`;

const JobDetailsCard = styled.div`
  background: linear-gradient(to bottom, #ffffff, #fafafa);
  border-radius: 12px;
  padding: 28px 32px;
  margin-top: 20px;
  border: 1px solid #f0f0f0;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.02);
`;

const CompanyBadge = styled.div`
  background: linear-gradient(135deg, #efc73c 0%, #bb9638 100%);
  color: #333;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 1.1rem;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 10px;
`;

const PublicJobBoard = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [applyingJobId, setApplyingJobId] = useState(null);
  const [showLoginForm, setShowLoginForm] = useState(false);

  // Fetch real jobs from API
  const fetchPublicJobs = async () => {
    try {
      setLoading(true);
      // Use the existing /jobs endpoint which returns all jobs publicly
      const response = await axiosInstant.get('/jobs');
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch public jobs:', error);
      message.error("Failed to load jobs");
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicJobs();
  }, []);

  // Handle pending job application after login
  useEffect(() => {
    const handlePendingApplication = async () => {
      const pendingJobId = localStorage.getItem('pendingJobApplication');
      if (user?.token && pendingJobId) {
        // User just logged in and there's a pending job application
        localStorage.removeItem('pendingJobApplication');
        
        // A brand new user has no category until they finish signing up, so send them
        // there with the application still pending rather than telling them to switch
        // to a candidate account - which makes no sense to someone who just registered.
        if (!user?.category) {
          localStorage.setItem('pendingJobApplication', pendingJobId);
          navigate(ROUTES.FINISH_SIGNUP);
          return;
        }
        if (user.category !== 'candidate') {
          message.error('Only candidates can apply for jobs. Please switch to a candidate account.');
          return;
        }
        
        try {
          await createJobApplication(pendingJobId);
          // createJobApplication already handles the redirect to candidate dashboard
        } catch (error) {
          console.error('Failed to create job application after login:', error);
          message.error('Failed to create job application. Please try again.');
        }
      }
    };

    handlePendingApplication();
  }, [user?.token]); // Watch for user token changes

  // Filter jobs based on search and filters
  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.positionTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.jobDescription?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLocation = selectedLocation === "all" || 
                           job.location?.toLowerCase().includes(selectedLocation.toLowerCase()) ||
                           (selectedLocation === "remote" && job.workType?.toLowerCase() === "remote");
    
    const matchesType = selectedType === "all" || job.workType?.toLowerCase() === selectedType.toLowerCase();
    
    return matchesSearch && matchesLocation && matchesType;
  });

  const handleQuickApply = async (jobId) => {
    // Check if user is logged in
    if (!user?.token) {
      // Store the jobId for after login
      localStorage.setItem('pendingJobApplication', jobId);
      setShowLoginForm(true);
      return;
    }
    
    // A brand new user has no category until they finish signing up, so send them
    // there with the application still pending rather than telling them to switch
    // to a candidate account - which makes no sense to someone who just registered.
    if (!user?.category) {
      localStorage.setItem('pendingJobApplication', jobId);
      navigate(ROUTES.FINISH_SIGNUP);
      return;
    }
    if (user.category !== 'candidate') {
      message.error('Only candidates can apply for jobs. Please switch to a candidate account.');
      return;
    }
    
    // User is logged in and is a candidate, create application and redirect to candidate dashboard
    await createJobApplication(jobId);
  };

  const createJobApplication = async (jobId) => {
    try {
      setApplyingJobId(jobId);
      
      // Create job application (draft status)
      const response = await axiosInstant.post('/job-applications/apply', {
        jobId: jobId,
        candidateId: user._id
      });
      
      message.success('Job added to your applications! Please complete your profile and answer screening questions to submit.');
      
      // Redirect to candidate dashboard where they can complete profile and answer questions
      setTimeout(() => {
        navigate('/candidate/dashboard');
      }, 1500);
      
    } catch (error) {
      console.error('Failed to add job application:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.status === 409) {
        message.warning('You have already applied for this job!');
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to add job application. Please try again.');
      }
    } finally {
      setApplyingJobId(null);
    }
  };



  const toggleJobDetails = (jobId) => {
    setExpandedJobs(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  const buildShareContent = (jobId, jobTitle, companyName) => {
    const url = `${window.location.origin}/jobs/${jobId}`;
    const text = `Check out this job: ${jobTitle} at ${companyName}`;
    return (
      <div style={{ minWidth: 160 }}>
        <p style={{ marginBottom: 8, fontWeight: 600, fontSize: 13 }}>Share this job</p>
        <Button
          type="text"
          icon={<LinkOutlined />}
          style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 4 }}
          onClick={() => { navigator.clipboard.writeText(url); message.success('Link copied!'); }}
        >
          Copy Link
        </Button>
        <Button
          type="text"
          icon={<LinkedinOutlined />}
          style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 4 }}
          onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=500')}
        >
          LinkedIn
        </Button>
        <Button
          type="text"
          icon={<TwitterOutlined />}
          style={{ display: 'block', width: '100%', textAlign: 'left' }}
          onClick={() => window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text + ' ' + url)}`, '_blank', 'width=600,height=500')}
        >
          X (Twitter)
        </Button>
      </div>
    );
  };

  const formatJobDescription = (description) => {
    if (!description) return '';
    
    // Split by newlines to process line by line
    const lines = description.split('\n');
    const elements = [];
    let currentList = [];
    let listKey = 0;
    
    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${listKey++}`} style={{ 
            margin: '18px 0 18px 28px', 
            padding: 0,
            listStyleType: 'none'
          }}>
            {currentList.map((item, idx) => (
              <li key={idx} style={{
                position: 'relative',
                paddingLeft: '28px',
                marginBottom: '12px',
                fontSize: '17px',
                lineHeight: '1.8',
                color: '#3a3a3a'
              }}>
                <span style={{
                  position: 'absolute',
                  left: 0,
                  top: '10px',
                  width: '10px',
                  height: '10px',
                  backgroundColor: '#efc73c',
                  borderRadius: '50%'
                }} />
                {item}
              </li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };
    
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) {
        flushList();
        return;
      }
      
      // Check if it's a section heading (common patterns)
      const headingPatterns = [
        /^(About\s*(Us|The\s*Company|The\s*Role)?|Company\s*Overview|Job\s*Description|Job\s*Responsibilities|Key\s*Responsibilities|Responsibilities|Requirements|Qualifications|Required\s*Qualifications|Preferred\s*Qualifications|What\s*You('ll|'ll|\s*Will)\s*(Do|Bring|Need)|What\s*We('re|'re|\s*Are)\s*Looking\s*For|Benefits|Perks|Skills|Required\s*Skills|Technical\s*Skills|Experience|Why\s*Join\s*Us|Our\s*Team|The\s*Opportunity|Role\s*Overview|Position\s*Summary|Summary|Overview|Who\s*You\s*Are|Your\s*Role|Your\s*Responsibilities|Nice\s*to\s*Have|Bonus\s*Points):?\s*$/i
      ];
      
      const isHeading = headingPatterns.some(pattern => pattern.test(trimmed)) || 
                        (trimmed.endsWith(':') && trimmed.length < 50 && !trimmed.match(/^[-•*]\s/));
      
      if (isHeading) {
        flushList();
        const headingText = trimmed.replace(/:$/, '');
        elements.push(
          <h4 key={`heading-${index}`} style={{ 
            margin: '32px 0 18px 0', 
            color: '#1a1a1a', 
            fontWeight: 700,
            fontSize: '20px',
            letterSpacing: '0.3px',
            textTransform: 'capitalize'
          }}>
            {headingText}
          </h4>
        );
        return;
      }
      
      // Check if it's a bullet point
      const bulletMatch = trimmed.match(/^[-•*▪▸►◆]\s*(.+)/) || 
                          trimmed.match(/^\d+[.)]\s*(.+)/) ||
                          trimmed.match(/^[a-zA-Z][.)]\s*(.+)/);
      
      if (bulletMatch) {
        currentList.push(bulletMatch[1] || trimmed.replace(/^[-•*▪▸►◆]\s*/, '').replace(/^\d+[.)]\s*/, '').replace(/^[a-zA-Z][.)]\s*/, ''));
        return;
      }
      
      // Regular paragraph
      flushList();
      elements.push(
        <p key={`para-${index}`} style={{ 
          margin: '16px 0', 
          lineHeight: '1.8', 
          color: '#3a3a3a',
          fontSize: '17px',
          fontWeight: 400
        }}>
          {trimmed}
        </p>
      );
    });
    
    flushList(); // Flush any remaining list items
    
    return elements;
  };

  return (
    <div>
      <Navbar />
      


    <JobBoardContainer>
      {/* Hero Search Section */}
      <SearchSection>
        <h1 style={{ fontSize: "3.5rem", fontWeight: "700", marginBottom: "20px", color: "#333" }}>
          Find Your Dream Job
        </h1>
        <p style={{ fontSize: "1.2rem", marginBottom: "30px", opacity: 0.8, color: "#444" }}>
          Discover opportunities from top companies and apply with one click
        </p>
        <Search
          placeholder="Search jobs, companies, or skills..."
          size="large"
          onSearch={setSearchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: 600, margin: "0 auto" }}
          enterButton={
            <Button 
              type="primary" 
              size="large"
              style={{ background: "#333", color: "#efc73c", border: "none", fontWeight: "600" }}
            >
              Search Jobs
            </Button>
          }
        />
      </SearchSection>

      {/* Filters */}
      <FilterSection>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <label style={{ fontWeight: "600", marginRight: "10px" }}>Location:</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #d9d9d9",
                width: "100%"
              }}
            >
              <option value="all">All Locations</option>
              <option value="remote">Remote</option>
              {[...new Set(jobs.map(j => j.location).filter(l => l && l.toLowerCase() !== 'remote'))].sort().map(loc => (
                <option key={loc} value={loc.toLowerCase()}>{loc}</option>
              ))}
            </select>
          </Col>
          <Col xs={24} sm={8}>
            <label style={{ fontWeight: "600", marginRight: "10px" }}>Job Type:</label>
            <select 
              value={selectedType} 
              onChange={(e) => setSelectedType(e.target.value)}
              style={{ 
                padding: "8px 12px", 
                borderRadius: "8px", 
                border: "1px solid #d9d9d9",
                width: "100%"
              }}
            >
              <option value="all">All Types</option>
              <option value="onsite">Onsite</option>
              <option value="hybrid">Hybrid</option>
              <option value="remote">Remote</option>
            </select>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: "center" }}>
              <strong style={{ color: "#efc73c", fontSize: "1.1rem" }}>
                {filteredJobs.length} jobs found
              </strong>
            </div>
          </Col>
        </Row>
      </FilterSection>

      {/* Job Listings */}
      <Row gutter={[24, 24]}>
        {loading ? (
          <Col xs={24} style={{ textAlign: "center", padding: "60px 20px" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: "#666" }}>Loading jobs...</p>
          </Col>
        ) : (
          filteredJobs.map(job => (
            <Col xs={24} key={job._id}>
              {/* Job Header Card */}
              <JobHeaderCard 
                onClick={() => navigate(`/jobs/${job._id}`)}
                style={{ cursor: 'pointer' }}
              >
                <Row justify="space-between" align="top">
                  <Col xs={24} md={16}>
                    <CompanyBadge>{job.companyName}</CompanyBadge>
                    <h2 style={{ 
                      margin: "8px 0 12px 0", 
                      fontSize: "1.5rem", 
                      fontWeight: "700",
                      color: "#333"
                    }}>
                      {job.positionTitle || job.title}
                    </h2>
                    
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", marginBottom: "16px", alignItems: "center" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#555", fontSize: "16px" }}>
                        <EnvironmentOutlined style={{ color: "#efc73c", fontSize: "20px" }} />
                        <strong>{job.location}</strong>
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#555", fontSize: "16px" }}>
                        <ClockCircleOutlined style={{ color: "#efc73c", fontSize: "20px" }} />
                        <strong>{job.workType}</strong>
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#555", fontSize: "16px" }}>
                        <DollarOutlined style={{ color: "#efc73c", fontSize: "20px" }} />
                        <strong>{job.salary}</strong>
                      </span>
                    </div>
                    
                    <p style={{
                      margin: "0 0 16px 0",
                      fontSize: "0.9rem",
                      color: "#999"
                    }}>
                      Posted {new Date(job.createdAt).toLocaleDateString()}{job.recruiterName ? ` by ${job.recruiterName}` : ''}
                    </p>
                  </Col>
                  
                  <Col xs={24} md={8}>
                    <div style={{ 
                      display: "flex", 
                      flexDirection: "column", 
                      gap: "10px", 
                      alignItems: "stretch"
                    }}>
                      {/* Primary CTA - Quick Apply */}
                      <ApplyButton 
                        type="primary" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickApply(job._id);
                        }}
                        loading={applyingJobId === job._id}
                        style={{ 
                          height: "48px", 
                          fontSize: "17px", 
                          fontWeight: 600,
                          borderRadius: "8px",
                          width: "100%"
                        }}
                      >
                        {applyingJobId === job._id ? 'Applying...' : 'Quick Apply'}
                      </ApplyButton>
                      
                      {/* Secondary actions row */}
                      <div style={{ display: "flex", gap: "10px" }}>
                        <Button
                          type="default"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleJobDetails(job._id);
                          }}
                          style={{
                            flex: 1,
                            border: "1px solid #d9d9d9",
                            color: "#595959",
                            height: "40px",
                            fontSize: "15px",
                            fontWeight: 500,
                            borderRadius: "8px",
                            background: "#fafafa"
                          }}
                          icon={<DownOutlined style={{ 
                            transform: expandedJobs[job._id] ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease',
                            fontSize: "12px"
                          }} />}
                        >
                          {expandedJobs[job._id] ? 'Hide Details' : 'View Details'}
                        </Button>
                        
                        <Popover
                          content={buildShareContent(job._id, job.positionTitle || job.title, job.companyName)}
                          trigger="click"
                          placement="bottomRight"
                        >
                          <Button
                            type="default"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              border: "1px solid #d9d9d9",
                              color: "#595959",
                              height: "40px",
                              fontSize: "15px",
                              fontWeight: 500,
                              borderRadius: "8px",
                              background: "#fafafa",
                              padding: "0 16px"
                            }}
                            icon={<ShareAltOutlined style={{ fontSize: "14px" }} />}
                          >
                            Share
                          </Button>
                        </Popover>
                      </div>
                    </div>
                  </Col>
                </Row>
                
                {/* Expandable Job Details */}
                {expandedJobs[job._id] && (
                  <JobDetailsCard>
                    <div>
                      <h3 style={{ 
                        color: "#1a1a1a", 
                        fontSize: "22px", 
                        fontWeight: 700,
                        marginBottom: "24px",
                        paddingBottom: "14px",
                        borderBottom: "3px solid #efc73c",
                        display: "inline-block"
                      }}>
                        Job Description
                      </h3>
                      <div style={{ 
                        marginTop: "12px",
                        maxWidth: "900px"
                      }}>
                        {formatJobDescription(job.jobDescription || job.description)}
                      </div>
                    
                      {job.benefits && (
                        <div style={{ marginTop: "40px" }}>
                          <h3 style={{ 
                            color: "#1a1a1a", 
                            fontSize: "22px", 
                            fontWeight: 700,
                            marginBottom: "24px",
                            paddingBottom: "14px",
                            borderBottom: "3px solid #efc73c",
                            display: "inline-block"
                          }}>
                            Benefits & Perks
                          </h3>
                          <div style={{ 
                            marginTop: "12px",
                            maxWidth: "900px"
                          }}>
                            {formatJobDescription(job.benefits)}
                          </div>
                        </div>
                      )}
                    </div>
                  </JobDetailsCard>
                )}
              </JobHeaderCard>
          </Col>
          ))
        )}
      </Row>

      {!loading && filteredJobs.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <h3 style={{ color: "#666", fontSize: "1.5rem" }}>No jobs found</h3>
          <p style={{ color: "#999" }}>Try adjusting your search criteria or filters</p>
        </div>
      )}
    </JobBoardContainer>
      
      {/* Login Form Modal - same as navbar */}
      {showLoginForm && (
        <AuthFormModal onCancel={() => { localStorage.removeItem('pendingJobApplication'); setShowLoginForm(false); }} />
      )}
      
      <Footer />
    </div>
  );
};

export default PublicJobBoard;